// src/pages/Login.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import api from '../api';
import { motion } from 'framer-motion';
import { GoogleLogin } from '@react-oauth/google'; // Provider va en App.jsx
import './Login.css';
import illustration from '../assets/login-illustration.jpeg';

export default function Login() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd]   = useState(false);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirectTo = params.get('redirectTo') || '/plans';

  // ✅ Si ya hay token, entra directo (no mostrar login)
  useEffect(() => {
    const token = localStorage.getItem('api_token');
    if (token) navigate(redirectTo, { replace: true });
  }, [navigate, redirectTo]);

  const submit = async e => {
    e.preventDefault();
    setError('');
    try {
      setLoading(true);
      const { data } = await api.post('/login', { email, password });

      // Guarda token y nombre de usuario
      localStorage.setItem('api_token', data.token);
      localStorage.setItem('user_name', data.user?.name || '');
      // El interceptor de api.js pondrá el header en futuras requests

      navigate('/plans', { replace: true }); // o navigate(redirectTo, { replace: true })
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Credenciales inválidas. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // === Google Login ===
  const onGoogleSuccess = async (credentialResponse) => {
    setError('');
    try {
      setGoogleLoading(true);
      const credential = credentialResponse?.credential;
      if (!credential) {
        setError('No se recibió el token de Google.');
        return;
      }

      const { data } = await api.post('/oauth/google', { credential });

      // Auto-login
      localStorage.setItem('api_token', data.token);
      localStorage.setItem('user_name', data.user?.name || '');
      // El interceptor de api.js usará el token en siguientes llamadas

      navigate('/plans', { replace: true }); // o navigate(redirectTo, { replace: true })
    } catch (err) {
      console.error(err);
      const serverMessage = err.response?.data?.message || 'No se pudo iniciar sesión con Google';
      setError(serverMessage);
    } finally {
      setGoogleLoading(false);
    }
  };

  const onGoogleError = () => {
    setError('Google no pudo autenticarte. Intenta de nuevo.');
  };

  return (
    <div className="login-page">
      <motion.div
        className="login-left"
        initial={{ x: -200, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="login-logo">Escuela Online</div>
        <img
          src={illustration}
          alt="Ilustración de bienvenida"
          className="login-illu"
        />
      </motion.div>

      <motion.div
        className="login-right"
        initial={{ x: 200, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <h2 className="login-title">¡Bienvenido de nuevo!</h2>

        {error && <div className="login-error">{error}</div>}

        <form onSubmit={submit} className="login-form">
          <div className="field-group">
            <input
              type="email"
              placeholder="Correo electrónico"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="field-group password-group">
            <input
              type={showPwd ? 'text' : 'password'}
              placeholder="Contraseña"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className="toggle-pwd"
              onClick={() => setShowPwd(!showPwd)}
              aria-label={showPwd ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
              {showPwd ? '🙈' : '👁️'}
            </button>
          </div>

          <button type="submit" className="btn login-btn" disabled={loading || googleLoading}>
            {loading ? 'Entrando…' : 'Iniciar sesión'}
          </button>
        </form>

        {/* Divider */}
        <div style={{ display: 'grid', placeItems: 'center', margin: '14px 0', opacity: 0.7 }}>
          <span>o</span>
        </div>

        {/* Google Login (provider viene desde App.jsx) */}
        <div style={{ display: 'grid', placeItems: 'center' }}>
          <GoogleLogin onSuccess={onGoogleSuccess} onError={onGoogleError} useOneTap={false} />
          {googleLoading && (
            <div style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
              Conectando con Google…
            </div>
          )}
        </div>

        <div className="login-links">
          <Link to="/forgot">Recordar contraseña</Link>
          <span> | </span>
          <Link to="/register">¿Todavía no tienes una cuenta? Regístrate</Link>
        </div>
      </motion.div>
    </div>
  );
}
